// src/utils/consentStorage.js

export const CONSENT_VERSION = 1;

const KEY = "cookie_consent_v1";

export const defaultConsent = {
  version: CONSENT_VERSION,
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
  updatedAt: null,
};

function canUseDocument() {
  return typeof document !== "undefined";
}

function canUseWindow() {
  return typeof window !== "undefined";
}

function canUseLocalStorage() {
  if (!canUseWindow()) {
    return false;
  }

  try {
    const testKey = "__consent_storage_test__";

    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);

    return true;
  } catch {
    return false;
  }
}

function getCookie(name) {
  if (!canUseDocument()) {
    return null;
  }

  try {
    const escapedName = name.replace(
      /[-[\]/{}()*+?.\\^$|]/g,
      "\\$&"
    );

    const match = document.cookie.match(
      new RegExp(`(?:^|; )${escapedName}=([^;]*)`)
    );

    return match
      ? decodeURIComponent(match[1])
      : null;
  } catch {
    return null;
  }
}

function setCookie(name, value) {
  if (!canUseDocument() || !canUseWindow()) {
    return;
  }

  try {
    const oneYear = 60 * 60 * 24 * 365;

    const hostname =
      window.location.hostname || "";

    const isProductionDomain =
      hostname === "reignitespirit.com" ||
      hostname.endsWith(".reignitespirit.com");

    const isHttps =
      window.location.protocol === "https:";

    const cookieParts = [
      `${name}=${encodeURIComponent(value)}`,
      `Max-Age=${oneYear}`,
      "Path=/",
      "SameSite=Lax",
    ];

    /*
      Use the shared production domain only on the
      ReigniteSpirit production website.

      Do not add Domain=.reignitespirit.com on localhost.
    */
    if (isProductionDomain) {
      cookieParts.push("Domain=.reignitespirit.com");
    }

    /*
      Secure cookies only work over HTTPS.
      Do not add Secure during normal localhost HTTP testing.
    */
    if (isHttps) {
      cookieParts.push("Secure");
    }

    document.cookie = cookieParts.join("; ");
  } catch (error) {
    console.warn(
      "Unable to save consent cookie:",
      error
    );
  }
}

function parseConsent(raw) {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);

    if (
      !parsed ||
      parsed.version !== CONSENT_VERSION
    ) {
      return null;
    }

    return {
      ...defaultConsent,
      ...parsed,
      necessary: true,
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

export function readConsent() {
  /*
    During prerendering or SSR there is no usable browser
    storage. Return safe defaults instead.
  */
  if (!canUseWindow()) {
    return {
      ...defaultConsent,
    };
  }

  try {
    if (canUseLocalStorage()) {
      const localRaw =
        window.localStorage.getItem(KEY);

      const localConsent =
        parseConsent(localRaw);

      if (localConsent) {
        return localConsent;
      }
    }

    const cookieRaw = getCookie(KEY);
    const cookieConsent =
      parseConsent(cookieRaw);

    if (cookieConsent) {
      if (canUseLocalStorage()) {
        try {
          window.localStorage.setItem(
            KEY,
            JSON.stringify(cookieConsent)
          );
        } catch {
          // Ignore storage synchronization failure.
        }
      }

      return cookieConsent;
    }

    return {
      ...defaultConsent,
    };
  } catch {
    return {
      ...defaultConsent,
    };
  }
}

export function writeConsent(consent) {
  if (!canUseWindow()) {
    return;
  }

  try {
    const normalized = {
      ...defaultConsent,
      ...consent,

      necessary: true,
      version: CONSENT_VERSION,

      updatedAt:
        consent?.updatedAt ||
        new Date().toISOString(),
    };

    const serialized =
      JSON.stringify(normalized);

    if (canUseLocalStorage()) {
      try {
        window.localStorage.setItem(
          KEY,
          serialized
        );
      } catch (error) {
        console.warn(
          "Unable to save consent in localStorage:",
          error
        );
      }
    }

    setCookie(KEY, serialized);
  } catch (error) {
    console.warn(
      "Unable to save consent:",
      error
    );
  }
}

export function hasMadeChoice(consent) {
  return Boolean(
    consent &&
      consent.version === CONSENT_VERSION &&
      consent.necessary === true &&
      typeof consent.preferences === "boolean" &&
      typeof consent.analytics === "boolean" &&
      typeof consent.marketing === "boolean" &&
      consent.updatedAt
  );
}